import { setClassName } from '@change-corgi/core/error';
import { loadable } from '@change-corgi/core-react/loadable';

import type { AppRoute, AppRouteBeforeServerRenderContext } from '#src/shared/routes';

import { canEditPetition, Loading, petitionSlugCanonicalRedirect } from '#src/app/shared/routes';

/**
 * A sentinel thrown from the wrapped `redirect` below so the `catch` block can tell "a redirect
 * already happened" apart from any real error `petitionSlugCanonicalRedirect` might throw.
 */
class AlreadyRedirectedError extends Error {}
setClassName(AlreadyRedirectedError, 'AlreadyRedirectedError');

async function beforeServerRender(context: AppRouteBeforeServerRenderContext): Promise<void> {
	try {
		// `petitionSlugCanonicalRedirect` calls `redirect` (and only returns) when the slug in the URL
		// isn't canonical. Since `context.redirect` calls `res.redirect` under the hood, calling it a
		// second time (from `redirectUntreatedStartStrongUsers` below) would throw — so this throws a
		// sentinel from the wrapped `redirect` to stop right after the first redirect fires.
		await petitionSlugCanonicalRedirect({
			...context,
			redirect: (url, permanent) => {
				context.redirect(url, permanent);
				throw new AlreadyRedirectedError();
			},
		});
	} catch (err) {
		if (err instanceof AlreadyRedirectedError) return;
		throw err;
	}

	// Untreated (control) starters must never see the Start Strong page — behave as if they never
	// entered the experiment and send them straight to the dashboard instead.
	//
	// Loaded lazily via dynamic import (not a static top-level import): the route-extraction script
	// (config/webpack/common/routes/script.ts) evaluates this module in a plain ESM context with no
	// GraphQL loader, and the pageContext → pageData → *.graphql chain uses `require`, which throws
	// there. Deferring the import to request time keeps routes.config's static graph GraphQL-free.
	const { redirectUntreatedStartStrongUsers } = await import(
		/* webpackChunkName: "pages/starter-startStrong" */ '#src/app/pages/starter/startStrong/pageContext'
	);
	await redirectUntreatedStartStrongUsers(context);
}

export const ROUTES: readonly AppRoute[] = [
	{
		id: 'starter-start-strong',
		path: '/p/:slug/start-strong',
		beforeServerRender,
		component: loadable(async () => import(/* webpackChunkName: "pages/starter-startStrong" */ './index'), {
			exportName: 'StartStrong',
			fallback: Loading,
		}),
		restrictedAccess: {
			authOnly: true,
			role: ['staff', 'support', canEditPetition('slug')],
		},
		frame: {
			footer: false,
			header: true,
		},
		ssr: {
			enabled: true,
			seo: false,
		},
	},
];
